import { AlertTriangle, Building2, FileText, Upload, CheckCircle } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import BONavigation from "../../../components/admin/BONavigation";
import ImportClosuresSection from "./ImportClosuresSection";
import { LogColumn, LogTable, StatCard } from "./importShared";
import {
    importEtablissementsSirene,
    RowLog,
    SireneImportProgress,
    SireneImportResult,
} from "../../../interfaces/importSirene";

// ─────────────────────────────────────────────────────────────────────────────
// Types & constants
// ─────────────────────────────────────────────────────────────────────────────

type Phase = "idle" | "uploading" | "done" | "error";
type Tab = "created" | "multiSiret" | "updated" | "skipped" | "errors";

const SKIP_REASON_LABEL: Record<string, string> = {
    no_changes: "Déjà complet",
    no_identifier: "Pas d'identifiant",
    duplicate_row: "Doublon dans le fichier",
};

// ─────────────────────────────────────────────────────────────────────────────
// Donut SVG chart
// ─────────────────────────────────────────────────────────────────────────────

const COLORS = {
    created: "#16a34a",  // green-600
    updated: "#2563eb",  // blue-600
    skipped: "#d1d5db",  // gray-300
    errors:  "#dc2626",  // red-600
};

function DonutChart({ created, updated, skipped, errors }: SireneImportProgress) {
    const total = created + updated + skipped + errors;
    if (total === 0) return null;

    const r = 40;
    const cx = 50;
    const cy = 50;
    const circumference = 2 * Math.PI * r;

    const segments = [
        { key: "created", value: created, color: COLORS.created },
        { key: "updated", value: updated, color: COLORS.updated },
        { key: "skipped", value: skipped, color: COLORS.skipped },
        { key: "errors",  value: errors,  color: COLORS.errors  },
    ].filter((s) => s.value > 0);

    let offset = 0;
    const paths = segments.map((seg) => {
        const pct = seg.value / total;
        const dash = pct * circumference;
        const dashOffset = circumference - offset * circumference;
        offset += pct;
        return (
            <circle
                key={seg.key}
                cx={cx}
                cy={cy}
                r={r}
                fill="none"
                stroke={seg.color}
                strokeWidth={14}
                strokeDasharray={`${dash} ${circumference}`}
                strokeDashoffset={dashOffset}
                transform={`rotate(-90 ${cx} ${cy})`}
                style={{ transition: "stroke-dasharray 0.4s ease" }}
            />
        );
    });

    return (
        <svg viewBox="0 0 100 100" className="w-full h-full">
            {/* track */}
            <circle cx={cx} cy={cy} r={r} fill="none" stroke="#f3f4f6" strokeWidth={14} />
            {paths}
            {/* center label */}
            <text x={cx} y={cy - 4} textAnchor="middle" className="text-xs font-semibold" fontSize="12" fill="#111827">
                {total.toLocaleString("fr-FR")}
            </text>
            <text x={cx} y={cy + 10} textAnchor="middle" fontSize="7" fill="#6b7280">
                lignes
            </text>
        </svg>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Stacked bar
// ─────────────────────────────────────────────────────────────────────────────

function StackedBar({ created, updated, skipped, errors }: SireneImportProgress) {
    const total = created + updated + skipped + errors;
    if (total === 0) return null;
    const pct = (n: number) => `${((n / total) * 100).toFixed(1)}%`;

    const segments = [
        { label: "Créés",      value: created, color: "bg-green-600" },
        { label: "Mis à jour", value: updated, color: "bg-blue-600" },
        { label: "Ignorés",    value: skipped, color: "bg-gray-300" },
        { label: "Erreurs",    value: errors,  color: "bg-red-600"  },
    ].filter((s) => s.value > 0);

    return (
        <div className="space-y-1.5">
            {segments.map(({ label, value, color }) => (
                <div key={label} className="flex items-center gap-2">
                    <span className="w-20 shrink-0 text-xs text-gray-500 text-right">{label}</span>
                    <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                        <div
                            className={`h-full rounded-full ${color} transition-all duration-500`}
                            style={{ width: pct(value) }}
                        />
                    </div>
                    <span className="w-24 shrink-0 text-xs text-gray-700 tabular-nums">
                        {value.toLocaleString("fr-FR")}
                        <span className="text-gray-400 ml-1">({pct(value)})</span>
                    </span>
                </div>
            ))}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Column definitions
// ─────────────────────────────────────────────────────────────────────────────

const COLS_CREATED: LogColumn<RowLog>[] = [
    { header: "SIRET",    render: (l) => <span className="font-mono">{l.siret || "—"}</span> },
    { header: "SIREN",    render: (l) => <span className="font-mono">{l.siren || "—"}</span> },
    { header: "Nom",      render: (l) => l.name || <span className="text-gray-400">—</span> },
    { header: "Ville",    render: (l) => l.city || <span className="text-gray-400">—</span> },
    { header: "NAF",      render: (l) => (
        <span title={l.nafLabel} className="font-mono text-gray-500">{l.nafCode || "—"}</span>
    )},
    { header: "Relation", render: (l) => l.sibling ? (
        <span className="inline-flex items-center gap-2">
            <span className="inline-block rounded-full bg-violet-50 border border-violet-200 px-2 py-0.5 text-[11px] font-medium text-violet-700 whitespace-nowrap">
                Établ. supplémentaire
            </span>
            <span className="text-[11px] text-gray-500">
                <span className="font-mono">{l.sibling.siret || "—"}</span>
                {l.sibling.name && <span className="ml-1 text-gray-400">· {l.sibling.name}</span>}
                {l.sibling.city && <span className="ml-1 text-gray-400">({l.sibling.city})</span>}
            </span>
        </span>
    ) : null },
];

const COLS_MULTI_SIRET: LogColumn<RowLog>[] = [
    { header: "Nouveau SIRET",   render: (l) => <span className="font-mono font-medium">{l.siret || "—"}</span> },
    { header: "SIREN partagé",   render: (l) => <span className="font-mono text-violet-700">{l.siren || "—"}</span> },
    { header: "Nom",             render: (l) => l.name || <span className="text-gray-400">—</span> },
    { header: "Ville",           render: (l) => l.city || <span className="text-gray-400">—</span> },
    { header: "Établissement existant", render: (l) => l.sibling ? (
        <span className="inline-flex items-center gap-1.5 text-[11px]">
            <span className="font-mono text-gray-700">{l.sibling.siret || "—"}</span>
            {l.sibling.name && <span className="text-gray-500">· {l.sibling.name}</span>}
            {l.sibling.city && <span className="text-gray-400">({l.sibling.city})</span>}
        </span>
    ) : <span className="text-gray-300">—</span> },
];

const COLS_UPDATED: LogColumn<RowLog>[] = [
    { header: "SIRET",   render: (l) => <span className="font-mono">{l.siret || "—"}</span> },
    { header: "Nom",     render: (l) => l.name || <span className="text-gray-400">—</span> },
    { header: "Ville",   render: (l) => l.city || <span className="text-gray-400">—</span> },
    { header: "Champs remplis", render: (l) => (
        <div className="flex flex-wrap gap-1">
            {(l.updatedFields ?? []).map((f) => (
                <span key={f} className="inline-block rounded-full bg-blue-50 border border-blue-200 px-1.5 py-0.5 text-[11px] text-blue-700">
                    {f}
                </span>
            ))}
        </div>
    )},
];

const COLS_SKIPPED: LogColumn<RowLog>[] = [
    { header: "SIRET",   render: (l) => <span className="font-mono">{l.siret || "—"}</span> },
    { header: "Nom",     render: (l) => l.name || <span className="text-gray-400">—</span> },
    { header: "Ville",   render: (l) => l.city || <span className="text-gray-400">—</span> },
    { header: "NAF",     render: (l) => <span className="font-mono text-gray-500">{l.nafCode || "—"}</span> },
    { header: "Raison",  render: (l) => (
        <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${
            l.skipReason && l.skipReason !== "no_changes"
                ? "bg-amber-50 border border-amber-200 text-amber-700"
                : "bg-gray-50 border border-gray-200 text-gray-600"
        }`}>
            {SKIP_REASON_LABEL[l.skipReason ?? ""] ?? l.skipReason ?? "—"}
        </span>
    )},
];

const COLS_ERRORS: LogColumn<RowLog>[] = [
    { header: "SIRET",   render: (l) => <span className="font-mono">{l.siret || "—"}</span> },
    { header: "Nom",     render: (l) => l.name || <span className="text-gray-400">—</span> },
    { header: "Message", render: (l) => <span className="text-red-600">{l.error}</span> },
];

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────

export default function ImportEtablissements() {
    const [file, setFile] = useState<File | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [phase, setPhase] = useState<Phase>("idle");
    const [progress, setProgress] = useState<SireneImportProgress | null>(null);
    const [result, setResult] = useState<SireneImportResult | null>(null);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<Tab>("created");
    const abortRef = useRef(false);

    const fileLabel = useMemo(() => {
        if (!file) return "Aucun fichier sélectionné";
        const sizeMb = (file.size / (1024 * 1024)).toFixed(1);
        return `${file.name} (${sizeMb} Mo)`;
    }, [file]);

    const onPickFile = (picked: File | null) => {
        setFile(picked);
        setPhase("idle");
        setProgress(null);
        setResult(null);
        setErrorMsg(null);
    };

    const onStart = async () => {
        if (!file) return;
        abortRef.current = false;
        setPhase("uploading");
        setProgress(null);
        setResult(null);
        setErrorMsg(null);
        setActiveTab("created");

        try {
            await importEtablissementsSirene(file, (event) => {
                if (abortRef.current) return;
                if (event.type === "progress") {
                    setProgress(event.data);
                } else if (event.type === "done") {
                    setResult(event.data);
                    setProgress(event.data);
                    setPhase("done");
                    // Default to the most interesting non-empty tab
                    if (event.data.created > 0) setActiveTab("created");
                    else if (event.data.updated > 0) setActiveTab("updated");
                    else if (event.data.errors > 0) setActiveTab("errors");
                    else setActiveTab("skipped");
                } else if (event.type === "error") {
                    setErrorMsg(event.data.message);
                    setPhase("error");
                }
            });
            setPhase((p) => (p === "uploading" ? "done" : p));
        } catch (err: any) {
            setErrorMsg(err?.message || "Import échoué");
            setPhase("error");
        }
    };

    const stats = progress ?? { created: 0, updated: 0, skipped: 0, errors: 0, multiSiret: 0, totalRows: 0 };
    const processed = stats.created + stats.updated + stats.skipped + stats.errors;
    const pct = stats.totalRows > 0 ? Math.min(100, Math.round((processed / stats.totalRows) * 100)) : null;

    const tabs: { id: Tab; label: string; count: number; color: string; activeColor: string }[] = [
        { id: "created",    label: "Créés",              count: stats.created,    color: "text-green-700",  activeColor: "border-green-600 text-green-700"   },
        { id: "multiSiret", label: "Même SIREN",         count: stats.multiSiret, color: "text-violet-700", activeColor: "border-violet-600 text-violet-700" },
        { id: "updated",    label: "Mis à jour",         count: stats.updated,    color: "text-blue-700",   activeColor: "border-blue-600 text-blue-700"     },
        { id: "skipped",    label: "Ignorés",            count: stats.skipped,    color: "text-gray-600",   activeColor: "border-gray-500 text-gray-700"     },
        { id: "errors",     label: "Erreurs",            count: stats.errors,     color: "text-red-600",    activeColor: "border-red-600 text-red-600"       },
    ];

    return (
        <div className="min-h-screen bg-[#F5F0EB]">
            <div className="mx-auto max-w-7xl px-4 pb-16">
                {/* Header */}
                <div className="mb-6 pt-6">
                    <div className="flex items-center gap-2">
                        <Building2 className="h-5 w-5 text-gray-600" />
                        <div>
                            <h1 className="text-xl font-semibold text-black">Import établissements</h1>
                            <p className="text-xs text-gray-600 mt-0.5">
                                Importez un fichier{" "}
                                <code className="bg-gray-100 px-1 rounded text-xs">restaurants_boulangeries.csv</code>{" "}
                                pour créer ou compléter des établissements
                            </p>
                        </div>
                    </div>
                </div>

                <BONavigation />

                {/* Drop zone */}
                <div className="bg-white border border-gray-200 rounded-xs p-4 mb-6">
                    <div
                        className={`rounded-xs border-2 border-dashed p-8 text-center transition-colors ${
                            isDragging ? "border-gray-700 bg-gray-50" : "border-gray-200"
                        }`}
                        onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }}
                        onDragOver={(e)  => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }}
                        onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); }}
                        onDrop={(e) => {
                            e.preventDefault(); e.stopPropagation(); setIsDragging(false);
                            const dropped = e.dataTransfer.files?.[0];
                            if (dropped) onPickFile(dropped);
                        }}
                    >
                        <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
                            <Upload className="h-5 w-5 text-gray-600" />
                        </div>
                        <p className="text-sm font-medium text-gray-900">Glissez-déposez votre CSV ici</p>
                        <p className="text-xs text-gray-500 mt-1">ou</p>
                        <label className="mt-3 inline-flex cursor-pointer items-center justify-center rounded-xs border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-900 hover:bg-[#F9F6F3]">
                            Choisir un fichier
                            <input type="file" accept=".csv,text/csv" className="hidden"
                                onChange={(e) => onPickFile(e.target.files?.[0] || null)} />
                        </label>
                        <div className="mt-3 flex items-center justify-center gap-2 text-xs text-gray-600">
                            <FileText className="h-4 w-4 text-gray-400" />
                            <span>{fileLabel}</span>
                        </div>
                        <div className="mt-4">
                            <button
                                onClick={onStart}
                                disabled={!file || phase === "uploading"}
                                className={`rounded-xs px-4 py-2 text-xs font-semibold text-white ${
                                    !file || phase === "uploading"
                                        ? "cursor-not-allowed bg-gray-300"
                                        : "bg-black hover:bg-gray-900"
                                }`}
                            >
                                {phase === "uploading" ? "Import en cours…" : "Lancer l'import"}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Fatal error */}
                {phase === "error" && errorMsg && (
                    <div className="bg-white border border-red-200 rounded-xs p-4 mb-6">
                        <div className="flex items-start gap-2">
                            <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
                            <p className="text-xs text-red-600">{errorMsg}</p>
                        </div>
                    </div>
                )}

                {/* Progress bar (uploading) */}
                {phase === "uploading" && (
                    <div className="bg-white border border-gray-200 rounded-xs p-4 mb-6">
                        <p className="text-xs font-medium text-gray-700 mb-2">Import en cours…</p>
                        <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                            {pct !== null ? (
                                <div className="h-full rounded-full bg-black transition-all duration-300" style={{ width: `${pct}%` }} />
                            ) : (
                                <div className="h-full w-1/3 rounded-full bg-black animate-pulse" />
                            )}
                        </div>
                        {progress && (
                            <p className="text-[11px] text-gray-400 mt-1">
                                {(stats.created + stats.updated + stats.skipped + stats.errors).toLocaleString("fr-FR")} / {stats.totalRows.toLocaleString("fr-FR")} lignes traitées
                            </p>
                        )}
                    </div>
                )}

                {/* Results section — visible once we have any stats */}
                {(phase === "done" || (phase === "uploading" && progress)) && (
                    <>
                        {/* Summary row */}
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
                            <StatCard label="Lignes lues" value={stats.totalRows} />
                            <StatCard label="Créés"       value={stats.created} color="green" sub="nouveaux établissements" />
                            <StatCard label="Mis à jour"  value={stats.updated} color="blue"  sub="champs complétés" />
                            <StatCard label="Ignorés"     value={stats.skipped} color="gray"  sub="déjà complets" />
                            <StatCard label="Erreurs"     value={stats.errors}  color={stats.errors > 0 ? "red" : "gray"} />
                        </div>

                        {/* Charts */}
                        {phase === "done" && (
                            <div className="bg-white border border-gray-200 rounded-xs p-5 mb-6">
                                <div className="flex items-center gap-2 mb-4">
                                    {phase === "done" && <CheckCircle className="h-4 w-4 text-green-600" />}
                                    <h2 className="text-sm font-semibold text-gray-900">
                                        Répartition des {stats.totalRows.toLocaleString("fr-FR")} lignes
                                    </h2>
                                </div>
                                <div className="flex flex-col sm:flex-row gap-8 items-start">
                                    {/* Donut */}
                                    <div className="w-32 h-32 shrink-0">
                                        <DonutChart {...stats} />
                                    </div>
                                    {/* Stacked bars */}
                                    <div className="flex-1 w-full">
                                        <StackedBar {...stats} />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Detail tabs — only in done state */}
                        {phase === "done" && result && (
                            <div className="bg-white border border-gray-200 rounded-xs">
                                {/* Tab bar */}
                                <div className="border-b border-gray-200 px-4">
                                    <nav className="flex gap-6">
                                        {tabs.map((tab) => {
                                            const active = activeTab === tab.id;
                                            return (
                                                <button
                                                    key={tab.id}
                                                    onClick={() => setActiveTab(tab.id)}
                                                    className={`py-3 text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${
                                                        active
                                                            ? tab.activeColor
                                                            : "border-transparent text-gray-400 hover:text-gray-600"
                                                    }`}
                                                >
                                                    {tab.label}
                                                    <span className={`ml-1.5 inline-block rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                                                        active ? "bg-gray-100" : "bg-gray-50"
                                                    } ${tab.color}`}>
                                                        {tab.count.toLocaleString("fr-FR")}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </nav>
                                </div>

                                {/* Tab content */}
                                <div className="p-4">
                                    {activeTab === "created" && (
                                        <LogTable
                                            logs={result.logs.created}
                                            total={result.created}
                                            columns={COLS_CREATED}
                                        />
                                    )}
                                    {activeTab === "multiSiret" && (
                                        <LogTable
                                            logs={result.logs.created.filter((l) => !!l.sibling)}
                                            total={result.multiSiret}
                                            columns={COLS_MULTI_SIRET}
                                        />
                                    )}
                                    {activeTab === "updated" && (
                                        <LogTable
                                            logs={result.logs.updated}
                                            total={result.updated}
                                            columns={COLS_UPDATED}
                                        />
                                    )}
                                    {activeTab === "skipped" && (
                                        <>
                                            {/* Skipped breakdown */}
                                            <SkippedBreakdown logs={result.logs.skipped} total={result.skipped} />
                                            <LogTable
                                                logs={result.logs.skipped}
                                                total={result.skipped}
                                                columns={COLS_SKIPPED}
                                            />
                                        </>
                                    )}
                                    {activeTab === "errors" && (
                                        <LogTable
                                            logs={result.logs.errors}
                                            total={result.errors}
                                            columns={COLS_ERRORS}
                                        />
                                    )}
                                </div>
                            </div>
                        )}
                    </>
                )}

                <ImportClosuresSection />
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Skipped breakdown mini-chart
// ─────────────────────────────────────────────────────────────────────────────

function SkippedBreakdown({ logs, total }: { logs: RowLog[]; total: number }) {
    const noChanges  = logs.filter((l) => l.skipReason === "no_changes").length;
    const noId       = logs.filter((l) => l.skipReason === "no_identifier").length;
    const dup        = logs.filter((l) => l.skipReason === "duplicate_row").length;
    // extrapolate if logs are capped
    const ratio = total > 0 && logs.length > 0 ? total / logs.length : 1;
    const noChangesEst  = Math.round(noChanges  * ratio);
    const noIdEst       = Math.round(noId       * ratio);
    const dupEst        = Math.round(dup        * ratio);

    if (total === 0) return null;

    return (
        <div className="mb-4 flex flex-wrap gap-3">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs text-gray-600">
                <span className="inline-block w-2 h-2 rounded-full bg-gray-400" />
                Déjà complets : <strong>{noChangesEst.toLocaleString("fr-FR")}</strong>
            </span>
            {noIdEst > 0 && (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs text-amber-700">
                    <span className="inline-block w-2 h-2 rounded-full bg-amber-400" />
                    Sans identifiant : <strong>{noIdEst.toLocaleString("fr-FR")}</strong>
                </span>
            )}
            {dupEst > 0 && (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs text-amber-700">
                    <span className="inline-block w-2 h-2 rounded-full bg-amber-400" />
                    Doublons dans le fichier : <strong>{dupEst.toLocaleString("fr-FR")}</strong>
                </span>
            )}
        </div>
    );
}
